import { FILE_KIND_DEFINITIONS, decodeAs, encode } from "@southfields-digital/mxmz-file";
import { z } from "zod";
import { asObjectSchema, isPrimitiveKind } from "@southfields-digital/mxmz-schema";
//#region src/resourceKinds.ts
const RESOURCE_KINDS = ["translation-resource"];
const RESOURCE_KIND_DEFINITIONS = { "translation-resource": {
	...FILE_KIND_DEFINITIONS["translation-resource"],
	label: "Translation"
} };
/** The kind a resource is when its row predates the field. */
const DEFAULT_RESOURCE_KIND = "translation-resource";
const resourceKind = (kind) => RESOURCE_KIND_DEFINITIONS[kind];
const isResourceKindId = (value) => typeof value === "string" && Object.hasOwn(RESOURCE_KIND_DEFINITIONS, value);
//#endregion
//#region src/envelope.ts
const TRANSLATION = resourceKind(DEFAULT_RESOURCE_KIND);
const RESOURCE_MEDIA_TYPE = TRANSLATION.mediaType;
const RESOURCE_EXTENSION = TRANSLATION.extension;
const encodeResourceFile = (payload) => encode(DEFAULT_RESOURCE_KIND, payload);
const decodeResourceFile = (data) => decodeAs(DEFAULT_RESOURCE_KIND, data);
//#endregion
//#region src/migrate.ts
const migrations = [];
const SCHEMA_VERSION = migrations.length;
/** Unstamped is version 0: every artifact written before the marker existed. */
const versionOf = (raw) => typeof raw === "object" && raw !== null && "schemaVersion" in raw && typeof raw.schemaVersion === "number" ? raw.schemaVersion : 0;
/** Shape only: framing is the file's and validation is zod's. */
const migrate = (raw) => {
	const version = versionOf(raw);
	if (version > SCHEMA_VERSION) throw new Error(`Resource schema version ${version} is newer than this reader's ${SCHEMA_VERSION}`);
	return migrations.slice(version).reduce((value, step) => step(value), raw);
};
//#endregion
//#region src/packageResourceResponse.ts
const isReadable = (resource) => !("error" in resource);
//#endregion
//#region src/packageResources.ts
const packageResourceRefs = (entries) => Array.isArray(entries) ? entries.filter((entry) => !!entry && typeof entry === "object" && typeof entry.resourceId === "string" && typeof entry.versionMajor === "number") : [];
//#endregion
//#region src/parse.ts
const build = () => {
	const value = z.lazy(() => z.union([
		z.string(),
		z.number(),
		z.boolean(),
		z.null(),
		z.record(value),
		z.array(value)
	]));
	const primitive = z.object({
		kind: z.literal("text"),
		label: z.string().optional(),
		options: z.array(z.object({
			value: z.string(),
			label: z.string()
		})).optional()
	});
	const schema = z.object({
		kind: z.literal("object"),
		label: z.string().optional(),
		fields: z.record(primitive)
	});
	return z.object({
		id: z.string(),
		name: z.string(),
		schema,
		instances: z.array(z.object({
			key: z.string(),
			label: z.string(),
			data: z.record(value)
		})),
		schemaVersion: z.number().optional()
	});
};
let compiled;
/** Throws with the first thing that is wrong, rather than a serialized issue list. */
const parseResourceArtifact = (raw) => {
	compiled ??= build();
	const result = compiled.safeParse(raw);
	if (result.success) return result.data;
	const issue = result.error.issues[0];
	const at = issue?.path.join(".") || "the payload";
	throw new Error(`Not a resource: ${at} ${issue?.message ?? "is invalid"}`);
};
//#endregion
//#region src/read.ts
/** Migrate before validate, or the validator rejects a shape a migration would have fixed. */
const readResourceFile = (data) => parseResourceArtifact(migrate(decodeResourceFile(data)));
//#endregion
//#region src/resolveSelection.ts
const isInstanceArray = (value) => Array.isArray(value);
/** JSON off an asset: one published before the index existed has nothing to route by. */
const resourceInstances = (instances) => isInstanceArray(instances) ? instances : [];
const offeredFrom = (resources) => Object.fromEntries(resources.map((resource) => [resource.id, { instances: resourceInstances(resource.instances) }]));
const supplied = (selection, offered) => {
	const instances = {};
	const unresolved = [];
	for (const [resourceId, instanceId] of Object.entries(selection)) {
		const resource = Object.hasOwn(offered, resourceId) ? offered[resourceId] : null;
		if (!resource) {
			unresolved.push({
				resourceId,
				instanceId,
				reason: "unknown-resource"
			});
			continue;
		}
		const instance = resource.instances.find((candidate) => candidate.key === instanceId);
		if (!instance) {
			unresolved.push({
				resourceId,
				instanceId,
				reason: "unknown-instance"
			});
			continue;
		}
		instances[resourceId] = instance;
	}
	return {
		instances,
		unresolved
	};
};
const unsupplied = (selection, offered) => supplied(selection, offered).unresolved;
/** Never falls back and never blanks: what it omits is reported instead. */
const resolveSelection = (selection, offered) => {
	const { instances, unresolved } = supplied(selection, offered);
	const data = {};
	for (const [resourceId, instance] of Object.entries(instances)) if (instance.data) data[resourceId] = instance.data;
	else unresolved.push({
		resourceId,
		instanceId: instance.key,
		reason: "unknown-instance"
	});
	return {
		data,
		unresolved
	};
};
//#endregion
//#region src/translationSchema.ts
const isFlat = (entry) => isPrimitiveKind(entry[1].kind);
/** A local draft, so depth is dropped and the row still opens; published bytes are refused. */
const asTranslationSchema = (value) => {
	const { fields, ...rest } = asObjectSchema(value);
	return {
		...rest,
		fields: Object.fromEntries(Object.entries(fields).filter(isFlat))
	};
};
//#endregion
//#region src/types.ts
/** Marks a graphic's data source as a resource rather than a connector. */
const RESOURCE_PROVIDER_ID = "__resource__";
//#endregion
export { DEFAULT_RESOURCE_KIND, RESOURCE_EXTENSION, RESOURCE_KINDS, RESOURCE_KIND_DEFINITIONS, RESOURCE_MEDIA_TYPE, RESOURCE_PROVIDER_ID, SCHEMA_VERSION, asTranslationSchema, encodeResourceFile, isReadable, isResourceKindId, migrate, offeredFrom, packageResourceRefs, parseResourceArtifact, readResourceFile, resolveSelection, resourceInstances, resourceKind, unsupplied };

//# sourceMappingURL=index.js.map